/** Canonical Twilio call/leg status strings. */
export type TwilioCallStatusString =
  | 'queued'
  | 'init'
  | 'initiated'
  | 'ringing'
  | 'in-progress'
  | 'completed'
  | 'busy'
  | 'failed'
  | 'no-answer'
  | 'canceled';

/** Twilio leg statuses that mean the leg is still live (not yet terminal). */
export const TWILIO_ACTIVE_CALL_STATUSES = [
  'init',
  'initiated',
  'in-progress',
  'ringing',
  'queued',
] as const;

/**
 * Twilio leg statuses that ended WITHOUT a successful answer.
 * Distinct from {@link TWILIO_FINAL_CALL_STATUSES}, which also includes a normal
 * 'completed' hang-up. Used to decide a connection is dead but not answered.
 */
export const TWILIO_UNANSWERED_TERMINAL_STATUSES = [
  'failed',
  'busy',
  'no-answer',
  'canceled',
] as const;

/** All Twilio terminal statuses: the unanswered set plus a normal 'completed'. */
export const TWILIO_FINAL_CALL_STATUSES = [
  ...TWILIO_UNANSWERED_TERMINAL_STATUSES,
  'completed',
] as const;

/**
 * Whether a Twilio leg status indicates the leg is still in progress.
 * @param status - Twilio call status string
 */
export function isTwilioCallLegActive(status: string): boolean {
  return (TWILIO_ACTIVE_CALL_STATUSES as readonly string[]).includes(status);
}

/**
 * Whether a Twilio leg reached any terminal status (including a normal 'completed').
 * @param status - Twilio call status string
 */
export function isTwilioCallLegFinal(status: string): boolean {
  return (TWILIO_FINAL_CALL_STATUSES as readonly string[]).includes(status);
}

/**
 * Whether a Twilio leg ended without being answered (failed/busy/no-answer/canceled).
 * @param status - Twilio call status string
 */
export function isTwilioCallLegUnanswered(status: string): boolean {
  return (TWILIO_UNANSWERED_TERMINAL_STATUSES as readonly string[]).includes(
    status
  );
}
